import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Laptop, LogOut, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { api, errText } from "@/lib/api";

const when = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
};

export default function LoginDevicesCard() {
  const [devices, setDevices] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/devices");
      setDevices(data || []);
    } catch {
      // a freshly rotated cookie can lose the first call — retry once
      try {
        const { data } = await api.get("/auth/devices");
        setDevices(data || []);
      } catch {
        setDevices([]);
      }
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const signOutOthers = async () => {
    if (!window.confirm("Sign out of every other device? You'll stay signed in here.")) return;
    setBusy(true);
    try {
      const { data } = await api.post("/auth/sign-out-others");
      toast.success(data.signed_out ? `Signed out of ${data.signed_out} other device(s)` : "No other devices were signed in");
      setDevices(data.devices || []);
    } catch (err) {
      toast.error(errText(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-2xl border border-border p-6 md:p-8" data-testid="login-devices-card">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="flex items-center gap-2 font-display text-lg">
            <ShieldCheck className="h-4 w-4 text-primary" /> Where you're signed in
          </p>
          <p className="mt-1.5 text-[13px] text-muted-foreground">
            We email you whenever your account is used on a device we haven't seen before.
          </p>
        </div>
        <Button
          variant="outline"
          className="h-11 rounded-lg font-bold"
          onClick={signOutOthers}
          disabled={busy}
          data-testid="sign-out-others-btn"
        >
          <LogOut className="h-4 w-4 mr-2" /> {busy ? "Signing out…" : "Sign out everywhere else"}
        </Button>
      </div>

      <div className="mt-5 space-y-2">
        {devices.length === 0 && (
          <p className="text-[13px] text-muted-foreground" data-testid="login-devices-empty">No sign-ins recorded yet.</p>
        )}
        {devices.map((d, i) => (
          <div key={i} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-border px-4 py-3" data-testid={`login-device-${i}`}>
            <p className="flex items-center gap-2.5 text-[13px] font-semibold">
              <Laptop className="h-4 w-4 text-muted-foreground" /> {d.label}
              <span className="font-normal text-muted-foreground">
                {d.method === "google" ? "Google" : "Email"} · {d.ip}
              </span>
            </p>
            <span className="text-[12px] text-muted-foreground">Last used {when(d.last_seen)}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
